import { useEffect } from 'react';
import { useUIStore, type WizardStep } from '@stores/useUIStore';

/**
 * Map between the wizard's numeric step and the user-facing URL slug.
 *
 *   /                  → step 1 (room setup)
 *   /?step=objects     → step 2
 *   /?step=analyze     → step 3
 *
 * Slugs are user-facing because they appear in the address bar and in
 * Vercel Analytics' Pages report. The numeric step stays the source of
 * truth in the store; this hook is a thin sync layer on top.
 */
const SLUG_BY_STEP: Record<WizardStep, string | null> = {
  1: null,
  2: 'objects',
  3: 'analyze',
};

const STEP_BY_SLUG: Record<string, WizardStep> = {
  setup: 1,
  objects: 2,
  analyze: 3,
};

function readStepFromURL(): WizardStep | null {
  if (typeof window === 'undefined') return null;
  const slug = new URLSearchParams(window.location.search).get('step');
  if (!slug) return 1;
  return STEP_BY_SLUG[slug] ?? null;
}

function writeStepToURL(step: WizardStep): void {
  if (typeof window === 'undefined') return;
  const slug = SLUG_BY_STEP[step];
  const url = new URL(window.location.href);
  if (slug === null) {
    url.searchParams.delete('step');
  } else {
    url.searchParams.set('step', slug);
  }
  // Only push when the URL actually differs to avoid spurious history
  // entries / pageviews when the store re-emits the same step.
  if (url.toString() !== window.location.href) {
    window.history.pushState({}, '', url);
  }
}

/**
 * Two-way sync between `?step=` in the URL and `useUIStore.currentStep`.
 *
 * Mounted once at the App level. Each step change pushes a new history
 * entry, which Vercel's Web Analytics SDK detects as a route change and
 * counts as an additional pageview. This is what keeps a wizard session
 * from being a single-event "bounce" in the dashboard.
 */
export function useStepRouting(): void {
  const currentStep = useUIStore((s) => s.currentStep);
  const goToStep = useUIStore((s) => s.goToStep);

  // 1. On mount: hydrate store from URL if a valid `?step=` is present.
  //    Deep-links bypass the wizard's forward-lock by lifting highestStep
  //    to the requested step — a user typing `?step=analyze` is being
  //    intentional and gets analysis on the default bedroom preset.
  useEffect(() => {
    const fromURL = readStepFromURL();
    if (!fromURL) return;
    const state = useUIStore.getState();
    if (fromURL === state.currentStep) return;
    if (fromURL > state.highestStep) {
      useUIStore.setState({ highestStep: fromURL, currentStep: fromURL });
    } else {
      goToStep(fromURL);
    }
    // Run once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2. Push URL whenever the store's step changes.
  useEffect(() => {
    writeStepToURL(currentStep);
  }, [currentStep]);

  // 3. Listen for back/forward navigation.
  useEffect(() => {
    function onPopState() {
      const fromURL = readStepFromURL();
      if (fromURL && fromURL !== useUIStore.getState().currentStep) {
        goToStep(fromURL);
      }
    }
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [goToStep]);
}
